import { useCallback } from "react";
import { Dictionary, logger } from "cayo.ui";
import { IAjaxAction } from "cayo.ui/lib/esm/interfaces/actions/ajax-action";
import log from "loglevel";
import { Entity } from "@cayo/shared/api/types/cayo-graph";
import { externalSubmitFunc } from "@cayo/shared/components/Form/types";
import { IActionBuilder } from "@cayo/shared/scheme/actions";
import { bindingsUtils } from "@cayo/shared/scheme/bindings";
import { IPropertiesPanelSignals } from "@cayo/shared/services/propertiesPanelSignal.service";
import { ajaxUtils } from "@cayo/shared/utils/ajax";
import downloadUtils from "@cayo/shared/utils/download";
import { objectUtils } from "@cayo/shared/utils/object";
import { urlUtils } from "@cayo/shared/utils/url";
import { CollectActionParametersDialogProps } from "./types";
import { hasApprovalRequiredStatus } from "./utils/hasApprovalRequiredStatus";
import { showApprovalConfirmation } from "./utils/showApprovalConfirmation/showApprovalConfirmation";
import { modelUtils } from "../Form/modelUtils/utils";

export const useExternalSubmit = (props: CollectActionParametersDialogProps) => {
  const {
    externalObjects,
    submit,
    actionParameters,
    actionBuilder,
    items,
    isAdminProps,
    sendAllFields,
  } = props;
  const { uiInteraction } = actionBuilder;

  const onSubmit = useCallback<externalSubmitFunc>(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (object: any, originalModel?: any, skipErrorHandling?: boolean) => {
      if ((externalObjects?.length || isAdminProps) && submit) {
        const toSubmit = (o: Entity) => {
          const actualSubmit = objectUtils.cloneObject(submit)!;
          if (urlUtils.hasBindings(actualSubmit!.url!)) {
            actualSubmit!.url = bindingsUtils.resolveExpression(
              o as Dictionary,
              actualSubmit!.url!
            ) as string;
          }

          actualSubmit.url = actualSubmit.url!.replace(/^\//, "");

          let validateResult: ReturnType<typeof actionBuilder.buildSubmitWithResponse> | undefined =
            undefined;

          if (submit?.isBatch && !actionParameters?.objectIdPropName) {
            object = { ...object, ids: externalObjects!.map((o) => o.id) } as any;
          } else if (actionParameters?.objectIdPropName) {
            const body = submit?.body ? JSON.parse(submit.body) : undefined;

            if (originalModel) {
              if (sendAllFields) {
                object = { ...originalModel, ...object };
              }
              object = modelUtils.removeBlankProps(object, originalModel);
            }

            const userCommentField = items?.find((i) => i.name === "UserComment");
            if (userCommentField && !object["UserComment"]) {
              object = { ...object, UserComment: "None" };
            }

            const beforePrms = modelUtils.getBeforeValueParameters(object, originalModel);
            const prms = modelUtils.convertToNameValueDictionary({ ...object }, items!);

            if (prms.length === 0) {
              return Promise.resolve();
            }

            logger.debug(beforePrms);
            object = { parameters: [...body.parameters, ...prms] } as any;

            if (!object.parameters.find((c: any) => c.name === "CayoSelectedObjectsIds")) {
              // Backed will fail without it with error: Cannot index into a null array. See bug #18940
              object.parameters.push({ name: "CayoSelectedObjectsIds", value: "" });
            }

            if (object.parameters.find((c: any) => c.name === "LicenseOptions")) {
              const mailBoxType = object.parameters.find(
                (c: any) => c.name === "MailBoxType"
              )?.value;
              const licenseAssignOptions = originalModel["LicenseAssignOptions"];

              if ((mailBoxType === "3" || mailBoxType === "2") && licenseAssignOptions === "1") {
                // licenses won't apply to user without these params
                object.parameters.push({ name: "LicenseOptions_WasSet", value: "True" });
                object.parameters.push({
                  name: "LicenseAssignOptions",
                  value: licenseAssignOptions,
                });
              }
            }

            if (submit?.validationUrl) {
              const validateSubmit = objectUtils.cloneObject(actualSubmit);
              validateSubmit.validationUrl = validateSubmit.validationUrl?.replace(/^\//, "");
              validateSubmit.url = validateSubmit.validationUrl;
              delete validateSubmit.validationUrl;
              delete validateSubmit.response;

              validateResult = actionBuilder.buildSubmitWithResponse(validateSubmit, object);
            }

            if (beforePrms) {
              object = { parameters: [...object.parameters, beforePrms] };
            }
          }

          if (validateResult) {
            return (validateResult as any).then((response: any) => {
              log.debug(response);

              if (response.status >= 300) {
                return Promise.resolve(response);
              }

              const validationBodyPromise = response
                .clone()
                .json()
                .catch((error: unknown) => {
                  log.error("Failed to read validation response", error);
                  return undefined;
                });

              return ajaxUtils.getError(response).then(async (error) => {
                if (error.error) {
                  throw error.error_description;
                }

                const validationResponseBody = await validationBodyPromise;
                if (hasApprovalRequiredStatus(validationResponseBody)) {
                  uiInteraction.setLoading(false);
                  object = {
                    ...object,
                    parameters: await showApprovalConfirmation({
                      parameters: object.parameters,
                      uiInteraction,
                    }),
                  };
                  uiInteraction.setLoading(true);
                }

                const result = actionBuilder.buildSubmitWithResponse(actualSubmit, object);
                return result;
              });
            });
          }

          return actionBuilder.buildSubmitWithResponse(actualSubmit, object);
        };

        const objects = externalObjects?.length ? externalObjects : [{}];
        const submits = submit?.isBatch ? [toSubmit(objects[0])] : objects.map(toSubmit);

        uiInteraction.setLoading(true);
        return Promise.all(submits)
          .then(async (responses) => {
            if (responses[0] !== undefined) {
              try {
                await processResponses(submit, actionBuilder, props.signals, responses);
              } catch (e) {
                logger.error("useExternalSubmit processResponses failed", e);
                throw e;
              }
            }

            props?.onSaveComplete && props?.onSaveComplete();

            return Promise.resolve();
          })
          .finally(() => uiInteraction.setLoading(false));
      }

      return Promise.resolve();
    },
    [externalObjects]
  );

  return onSubmit;
};

const processResponses = async (
  submit: IAjaxAction,
  actionBuilder: IActionBuilder,
  signals: IPropertiesPanelSignals | undefined,
  responses: Response[]
) => {
  const response = ((responses as Response[]).find((r) => r.status >= 400) ||
    responses[0]) as Response;

  const headers: any = ajaxUtils.getHeaders(response);
  let result: any = {};

  if (
    headers["content-type"] === "application/octet-stream" ||
    headers["content-type"] === "text/plain"
  ) {
    result = await response.blob();
    log.debug("blob found");
  } else {
    try {
      result = await response.json();
      log.debug("json result", result);
    } catch (e) {
      log.error("Failed to read response", e);
    }
  }

  if (response.status > 300) {
    return Promise.reject(result);
  }

  if (submit?.isStream && result) {
    downloadUtils.downloadStream(result as Blob, headers);
    result = {};
  }

  // eslint-disable-next-line eqeqeq
  if (result.status == "error") {
    return Promise.reject(result);
  }

  try {
    const doNotThrowErrorOnSuccess = response.status === 200;
    actionBuilder.processResponse(
      result,
      undefined,
      submit!.response,
      undefined,
      signals,
      doNotThrowErrorOnSuccess
    );
  } catch (e) {
    return Promise.reject(e);
  }

  return Promise.resolve();
};
